/**
 * O estado do workspace mora na URL, não em memória.
 *
 * Recarregar a página cai no mesmo lugar, o botão voltar funciona e um link
 * leva outra pessoa exatamente ao que você está vendo — três coisas que se
 * perdem quando o layout guarda tudo num store. O preço é manter a leitura e a
 * escrita destes parâmetros num lugar só, que é este arquivo.
 */

/** Qual view ocupa a lateral esquerda. */
export type LadoAberto = 'explorador' | 'sessoes' | 'audio' | 'admin';

const LADOS: LadoAberto[] = ['explorador', 'sessoes', 'audio', 'admin'];

/** Qual das três abas o celular está mostrando. */
export type AbaDoCelular = 'sessoes' | 'chat' | 'menu';

const ABAS_DO_CELULAR: AbaDoCelular[] = ['sessoes', 'chat', 'menu'];

export interface EstadoDoWorkspace {
  /** Máquina escolhida no explorador. */
  maquina: string;
  /** Pasta aberta na árvore. */
  pasta: string;
  /** Arquivos abertos em aba, na ordem em que foram abertos. */
  abas: string[];
  /** Aba ativa; vazio quando nenhuma. */
  aba: string;
  /**
   * O segundo grupo de abas, quando a área está dividida.
   *
   * Vazio quer dizer "não dividida" — é o estado normal, e por isso ele não
   * aparece na URL. A divisão nasce de arrastar uma aba (ou um arquivo da
   * árvore) para o lado direito da área de edição.
   */
  abasB: string[];
  abaB: string;
  /** Sessão no painel da direita. */
  sessao: string;
  lado: LadoAberto;
  /**
   * A aba do celular — e o motivo dela morar aqui é o botão voltar.
   *
   * Enquanto era `useState` dentro do `NoCelular`, trocar de aba não mexia no
   * histórico: quem ia da lista para a conversa e voltava para a lista tinha
   * um só passo gravado, e o voltar do aparelho saltava o painel inteiro para
   * a última página de verdade. Relatado assim: "clico em voltar e vai para
   * outra tela". Na URL, cada passo é um passo.
   *
   * No desktop o campo existe e é ignorado — lá quem manda são os painéis.
   */
  celular: AbaDoCelular;
  /**
   * A Prosa está aberta? (`prosa=1`)
   *
   * Param próprio, e não um quarto valor de `celular`, por dois motivos. O
   * `celular` é ignorado no desktop e a Prosa vale nos dois; e este campo é
   * **ortogonal** ao resto — sair da Prosa é apagar o param, e o que estava
   * embaixo (a aba do celular, ou o workspace de três colunas com suas abas
   * abertas) reaparece exatamente como estava. Como valor de `celular`, a
   * saída teria de **adivinhar** para qual aba voltar.
   *
   * Vive na URL, e não em `localStorage`, porque é passo de navegação: o
   * voltar do aparelho tem de sair da Prosa, recarregar tem de cair nela de
   * novo, e o link colado tem de abrir onde quem colou estava. O que mora na
   * memória é outra pergunta — "é a Prosa que abre por padrão no celular?" —,
   * e ela é preferência de aparelho (ver `memoria.ts`).
   */
  prosa: boolean;
}

export const WORKSPACE_VAZIO: EstadoDoWorkspace = {
  maquina: '',
  pasta: '',
  abas: [],
  aba: '',
  abasB: [],
  abaB: '',
  sessao: '',
  lado: 'sessoes',
  celular: 'sessoes',
  prosa: false,
};

/**
 * O tipo do arrasto de aba.
 *
 * Tipo próprio, e não `text/plain`: assim arrastar texto de fora do painel
 * para dentro do editor não é confundido com mover um arquivo de lugar.
 */
export const TIPO_ARRASTO = 'application/x-remoto-aba';

/** Qual metade da área de edição. */
export type Metade = 'a' | 'b';

/**
 * O separador das abas é `\n`.
 *
 * Caminho do Windows tem `\`, dois-pontos e espaço; vírgula e ponto-e-vírgula
 * aparecem em nome de arquivo. A quebra de linha é o único caractere que o
 * sistema de arquivos não aceita em nenhum dos dois sistemas, então é o único
 * que nunca vai partir um caminho no meio.
 */
const SEPARADOR = '\n';

function texto(valor: unknown): string {
  return typeof valor === 'string' ? valor : '';
}

/** Lê o que veio na URL, aceitando qualquer lixo sem quebrar a tela. */
export function lerWorkspace(busca: Record<string, unknown>): EstadoDoWorkspace {
  const lado = texto(busca['lado']) as LadoAberto;
  const abas = texto(busca['abas']).split(SEPARADOR).filter(Boolean);
  const aba = texto(busca['aba']);
  const abasB = texto(busca['abasb']).split(SEPARADOR).filter(Boolean);
  const abaB = texto(busca['abab']);

  return {
    maquina: texto(busca['m']),
    pasta: texto(busca['p']),
    celular: ABAS_DO_CELULAR.includes(texto(busca['cel']) as AbaDoCelular)
      ? (texto(busca['cel']) as AbaDoCelular)
      : 'sessoes',
    abas,
    // Aba ativa que não está entre as abertas não existe: cair na última é
    // melhor que mostrar um editor vazio sem explicação.
    aba: abas.includes(aba) ? aba : (abas.at(-1) ?? ''),
    abasB,
    abaB: abasB.includes(abaB) ? abaB : (abasB.at(-1) ?? ''),
    sessao: texto(busca['s']),
    lado: LADOS.includes(lado) ? lado : 'sessoes',
    // `1` ou `'1'`, e nada mais.
    //
    // Os dois porque o roteador COAGE: `validateSearch` devolve a busca crua e
    // o TanStack Router ja converteu `?prosa=1` para o NUMERO 1. Aceitar so a
    // string fazia a tela nunca abrir por link — e o teste de unidade nao pegou,
    // porque ele passava strings. Quem pegou foi o ensaio de tela.
    prosa: busca['prosa'] === 1 || texto(busca['prosa']) === '1',
  };
}

/**
 * Escreve de volta, omitindo o que está vazio.
 *
 * URL curta é URL que dá para colar numa conversa; parâmetro vazio só polui.
 */
export function escreverWorkspace(estado: EstadoDoWorkspace): Record<string, string> {
  const busca: Record<string, string> = {};
  if (estado.maquina) busca['m'] = estado.maquina;
  if (estado.pasta) busca['p'] = estado.pasta;
  if (estado.abas.length > 0) busca['abas'] = estado.abas.join(SEPARADOR);
  if (estado.aba) busca['aba'] = estado.aba;
  if (estado.abasB.length > 0) busca['abasb'] = estado.abasB.join(SEPARADOR);
  if (estado.abaB) busca['abab'] = estado.abaB;
  if (estado.sessao) busca['s'] = estado.sessao;
  if (estado.lado !== 'sessoes') busca['lado'] = estado.lado;
  // Só sai na URL quando não é o padrão: no desktop o parâmetro nunca aparece.
  if (estado.celular !== 'sessoes') busca['cel'] = estado.celular;
  if (estado.prosa) busca['prosa'] = '1';
  return busca;
}

/** As abas e a ativa de uma das metades. */
export function abasDe(estado: EstadoDoWorkspace, metade: Metade): string[] {
  return metade === 'a' ? estado.abas : estado.abasB;
}

export function abaAtivaDe(estado: EstadoDoWorkspace, metade: Metade): string {
  return metade === 'a' ? estado.aba : estado.abaB;
}

function comAbas(
  estado: EstadoDoWorkspace,
  metade: Metade,
  abas: string[],
  aba: string,
): EstadoDoWorkspace {
  return metade === 'a' ? { ...estado, abas, aba } : { ...estado, abasB: abas, abaB: aba };
}

/** Abre um arquivo: vira a aba ativa, sem duplicar o que já estava aberto. */
export function abrirAbaEm(
  estado: EstadoDoWorkspace,
  metade: Metade,
  caminho: string,
): EstadoDoWorkspace {
  const atuais = abasDe(estado, metade);
  const abas = atuais.includes(caminho) ? atuais : [...atuais, caminho];
  return comAbas(estado, metade, abas, caminho);
}

export function abrirAba(estado: EstadoDoWorkspace, caminho: string): EstadoDoWorkspace {
  return abrirAbaEm(estado, 'a', caminho);
}

/**
 * Fecha uma aba e escolhe a vizinha.
 *
 * A vizinha da esquerda é a escolha certa: é para onde o olho vai depois de
 * fechar, e é o que todo editor faz.
 */
export function fecharAbaEm(
  estado: EstadoDoWorkspace,
  metade: Metade,
  caminho: string,
): EstadoDoWorkspace {
  const atuais = abasDe(estado, metade);
  const indice = atuais.indexOf(caminho);
  if (indice === -1) return estado;

  const abas = atuais.filter((a) => a !== caminho);
  const ativa = abaAtivaDe(estado, metade);
  const nova = ativa === caminho ? (abas[Math.max(0, indice - 1)] ?? '') : ativa;
  return comAbas(estado, metade, abas, nova);
}

export function fecharAba(estado: EstadoDoWorkspace, caminho: string): EstadoDoWorkspace {
  return fecharAbaEm(estado, 'a', caminho);
}

/**
 * Leva um arquivo para a outra metade.
 *
 * Ele sai de onde estava: duas cópias do mesmo arquivo lado a lado seriam dois
 * editores disputando o mesmo rascunho, e a segunda não teria utilidade
 * nenhuma. Se a metade de origem ficar vazia, a divisão some sozinha — e some
 * de verdade, sem deixar um painel vazio ocupando espaço.
 */
export function moverAba(
  estado: EstadoDoWorkspace,
  destino: Metade,
  caminho: string,
): EstadoDoWorkspace {
  const origem: Metade = destino === 'a' ? 'b' : 'a';
  if (abasDe(estado, destino).includes(caminho)) {
    return comAbas(estado, destino, abasDe(estado, destino), caminho);
  }
  const semOrigem = fecharAbaEm(estado, origem, caminho);
  const comDestino = abrirAbaEm(semOrigem, destino, caminho);
  return comDestino.abasB.length === 0 ? { ...comDestino, abaB: '' } : comDestino;
}

/**
 * O caminho de arquivo que um link da conversa aponta, se for um.
 *
 * O Claude escreve `[pat-da-vps.md](docs/roteiros/pat-da-vps.md)` o tempo todo.
 * Enquanto a bolha era texto puro isso não fazia nada; renderizado como
 * markdown virou um link relativo, e clicar nele **tirava a pessoa do painel**
 * para um endereço que não existe. Um caminho de arquivo não é um endereço da
 * web: ele abre numa aba do editor.
 *
 * Devolve `null` para o que é link de verdade (http, mailto, âncora) — esse
 * segue sendo link, só que em outra aba do navegador.
 */
export function caminhoDoLink(base: string, href: string): string | null {
  if (!href || href.startsWith('#')) return null;

  // A letra de disco do Windows é lida como esquema de URL — `C:` casa
  // `[a-z][a-z0-9+.-]*:` tão bem quanto `http:`. Por isso a checagem de
  // esquema tem de vir DEPOIS: sem esta linha, todo caminho absoluto do
  // Windows caía como "link de verdade" e nunca abria no editor, e o ramo de
  // caminho absoluto logo abaixo era código morto.
  const disco = /^[A-Za-z]:[\\/]/.test(href);
  if (!disco && /^[a-z][a-z0-9+.-]*:/i.test(href)) return null;

  const separador = base.includes('\\') ? '\\' : '/';
  const absoluto = href.startsWith('/') || disco;
  const partes = absoluto ? [] : base.split(/[\\/]/).filter(Boolean);

  for (const pedaco of href.split(/[\\/]/)) {
    if (pedaco === '' || pedaco === '.') continue;
    if (pedaco === '..') {
      // Subir além da raiz não é caminho: é lixo, e o daemon recusaria.
      if (partes.length === 0) return null;
      partes.pop();
      continue;
    }
    partes.push(pedaco);
  }
  if (partes.length === 0) return null;

  const junto = partes.join(separador);
  // No POSIX a barra inicial se perde no split e precisa voltar; no Windows a
  // letra do drive já vem no primeiro pedaço.
  return separador === '/' ? '/' + junto : junto;
}

/** Nome curto para a aba; o caminho inteiro fica no `title`. */
export function nomeDaAba(caminho: string): string {
  return caminho.split(/[\\/]/).filter(Boolean).at(-1) ?? caminho;
}

/**
 * Quando duas abas têm o mesmo nome, mostra também a pasta.
 *
 * Dois `package.json` abertos viram duas abas idênticas, e aí a aba deixa de
 * dizer qual arquivo é — que é a única coisa que ela precisa dizer.
 */
export function rotulosDasAbas(abas: string[]): Map<string, string> {
  const porNome = new Map<string, string[]>();
  for (const caminho of abas) {
    const nome = nomeDaAba(caminho);
    porNome.set(nome, [...(porNome.get(nome) ?? []), caminho]);
  }

  const rotulos = new Map<string, string>();
  for (const [nome, caminhos] of porNome) {
    for (const caminho of caminhos) {
      if (caminhos.length === 1) {
        rotulos.set(caminho, nome);
        continue;
      }
      const partes = caminho.split(/[\\/]/).filter(Boolean);
      const pai = partes.at(-2);
      rotulos.set(caminho, pai ? `${pai}/${nome}` : nome);
    }
  }
  return rotulos;
}
